/**
 * CV Data Compact - ATS Optimized for 2026
 *
 * Designed for:
 * - Maximum 1-2 pages when rendered
 * - ATS (Applicant Tracking Systems) parsing
 * - AI-powered resume screening
 * - Quantified achievements with metrics
 * - Action verbs (Implemented, Designed, Optimized, Led)
 *
 * Format: Reverse chronological, results-oriented
 */

export interface CVDataCompact {
  personal: {
    name: string;
    title: string;
    email: string;
    phone: string;
    linkedin: string;
    website: string;
    location: string;
    languages: string;
  };
  summary: string;
  skills: {
    category: string;
    items: string[];
  }[];
  experience: {
    role: string;
    company: string;
    location: string;
    period: string;
    achievements: string[];
  }[];
  education: {
    degree: string;
    institution: string;
    year: string;
  }[];
  certifications: string[];
}

export const cvDataCompact: CVDataCompact = {
  personal: {
    name: 'Fernando Olea',
    title: 'CTO & Tech Lead | XR Developer | Full-Stack AI Engineer',
    email: 'contacto@fernandoolea.com',
    phone: '+34 636 27 60 78',
    linkedin: 'linkedin.com/in/fernando-olea-lyceum',
    website: 'fernandoolea.com',
    location: 'Madrid, Spain | Open to Remote & Relocation',
    languages: 'Spanish (Native), English (Professional)',
  },

  summary: `**CTO and Tech Lead** with **9+ years** of experience delivering **XR (AR/VR/MR)** and **full-stack** solutions. Led teams of up to **15 developers**. Founded **Lyceum VR**, delivering **20+ international projects** for **Verizon**, **Arcadia Earth**, and **NY Fashion Week**. Expert in **Unity/C#** and **AI-Powered development** (React/TypeScript). Proven track record reducing development time by **40%** through AI integration.`,

  skills: [
    {
      category: 'AI & Development',
      items: [
        'AI-Assisted Development (Claude Code, Cursor)',
        'RAG & Embeddings',
        'LangChain/LangGraph',
        'OpenAI API',
        'Prompt Engineering',
        'Vector Databases',
      ],
    },
    {
      category: 'XR Development',
      items: [
        'Unity 3D (9+ years)',
        'C# Expert',
        'HoloLens 2 / Meta Quest',
        'WebXR',
        'Digital Twins',
        'ARFoundation / OpenXR',
      ],
    },
    {
      category: 'Full-Stack & Cloud',
      items: [
        'React / Next.js',
        'TypeScript / Node.js',
        'Supabase / PostgreSQL',
        'Docker / CI-CD',
        'AWS / Azure / GCP',
      ],
    },
    {
      category: 'Leadership',
      items: [
        'Team Leadership (15+ people)',
        'Technical Strategy',
        'Agile (Scrum/Kanban)',
        'Stakeholder Communication',
        'Product Mindset',
      ],
    },
  ],

  experience: [
    {
      role: 'CTO & Tech Lead',
      company: 'HausLook',
      location: 'Madrid, Spain',
      period: 'May 2025 - Present',
      achievements: [
        'Architected **full-stack platform** (React/TypeScript/Supabase) reducing time-to-market by **35%**',
        'Implemented **AI agents** for data management, cutting manual processing time by **60%**',
        'Developed **Unity WebGL 3D configurator** increasing customer engagement by **45%**',
        'Integrated **AI-assisted development** tools boosting team productivity by **40%**',
      ],
    },
    {
      role: 'Founder, CEO & CTO',
      company: 'Lyceum VR',
      location: 'Madrid, Spain',
      period: '2020 - Present',
      achievements: [
        'Founded and scaled **XR company**, leading teams of up to **15 developers** and artists',
        'Delivered **20+ international projects** for **Verizon**, **Arcadia Earth** (NY, Las Vegas, Toronto)',
        'Designed custom **location-tracking system** controlling **40+ HoloLens** simultaneously at **NY Fashion Week**',
        'Built **VR education platform** with **10+ experiences** deployed across multiple schools',
        'Developed **AR system** using OpenCV for non-ARCore devices, expanding market reach by **30%**',
      ],
    },
    {
      role: 'Researcher & Developer',
      company: 'CT Ingenieros',
      location: 'Madrid, Spain',
      period: '2019 - 2020',
      achievements: [
        'Led **R&D** for civil and military **simulation software** development',
        'Supervised **5+ university thesis** projects (undergraduate and master level)',
        'Implemented **real-time training scenarios** reducing training costs by **25%**',
      ],
    },
    {
      role: 'CTO',
      company: '6DLab',
      location: 'Madrid, Spain',
      period: '2019',
      achievements: [
        'Directed **AR/VR development team** establishing technical standards',
        'Implemented **Agile methodologies** improving delivery times by **30%**',
        'Delivered multiple **enterprise AR/VR projects** on time and budget',
      ],
    },
    {
      role: 'VR Developer & AR/VR Researcher',
      company: 'LudusVR',
      location: 'Madrid, Spain',
      period: '2017 - 2019',
      achievements: [
        'Developed **VR applications** for Industry 4.0 training reducing onboarding time by **40%**',
        'Pioneered **AR/VR research** initiatives for emerging technologies',
        'Collaborated on **digital twin** implementations for industrial clients',
      ],
    },
  ],

  education: [
    {
      degree: 'Master in AI for Developers (AI4Devs)',
      institution: 'LIDR',
      year: '2024 - 2025',
    },
    {
      degree: 'Bachelor in Computer Engineering',
      institution: 'Universidad de Alcalá de Henares',
      year: '2013 - 2017',
    },
  ],

  certifications: [
    'AI for Developers Specialization',
    'Unity Certified Developer',
    'Microsoft Mixed Reality Developer',
  ],
};

/**
 * Generate JSON-LD Schema.org structured data for SEO and AI crawlers
 */
export function generatePersonJsonLd(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: cvDataCompact.personal.name,
    jobTitle: cvDataCompact.personal.title,
    email: `mailto:${cvDataCompact.personal.email}`,
    telephone: cvDataCompact.personal.phone,
    url: `https://${cvDataCompact.personal.website}`,
    sameAs: [`https://${cvDataCompact.personal.linkedin}`],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Madrid',
      addressCountry: 'Spain',
    },
    knowsLanguage: ['Spanish', 'English'],
    hasOccupation: {
      '@type': 'Occupation',
      name: 'CTO & Tech Lead',
      occupationalCategory: 'Software Development',
      skills: cvDataCompact.skills.flatMap((s) => s.items).join(', '),
    },
    alumniOf: cvDataCompact.education.map((edu) => ({
      '@type': 'EducationalOrganization',
      name: edu.institution,
    })),
    worksFor: {
      '@type': 'Organization',
      name: cvDataCompact.experience[0].company,
    },
    description: cvDataCompact.summary,
  };
}

/**
 * ATS Keywords for resume optimization
 */
export const atsKeywordsCompact = [
  // Leadership
  'CTO',
  'Tech Lead',
  'Team Leadership',
  'Agile',
  'Scrum',

  // AI/ML
  'AI',
  'Machine Learning',
  'LLM',
  'RAG',
  'Embeddings',
  'Prompt Engineering',
  'OpenAI',
  'LangChain',

  // XR
  'Unity',
  'C#',
  'AR',
  'VR',
  'MR',
  'XR',
  'HoloLens',
  'Meta Quest',
  'WebXR',
  'Digital Twins',

  // Full-Stack
  'React',
  'Next.js',
  'TypeScript',
  'Node.js',
  'Python',
  'Docker',
  'Kubernetes',

  // Cloud
  'AWS',
  'Azure',
  'GCP',
  'CI/CD',
  'DevOps',
];
